import shpwrite from "@mapbox/shp-write";
import tokml from "tokml";
import { formatDate } from "utils";
import { downloadBlob } from "utils/file";
import { STRUCTURE_VALUES, VEGETATION_VALUES } from "constants/urbanNodes";

// Filter a GeoJSON FeatureCollection by pathway run_type.
export function filterPathwaysByRunType(featureCollection, runType) {
  if (!featureCollection?.features || runType === "all") {
    return featureCollection ?? { type: "FeatureCollection", features: [] };
  }
  const features = featureCollection.features.filter(
    (f) => f.properties?.run_type === runType
  );
  return { type: "FeatureCollection", features };
}

// Filter a GeoJSON FeatureCollection by node_type (urban).
export function filterFeaturesByNodeType(featureCollection, nodeType) {
  if (!featureCollection?.features || nodeType === "all") {
    return featureCollection ?? { type: "FeatureCollection", features: [] };
  }
  const allowed =
    nodeType === "structure" ? STRUCTURE_VALUES : VEGETATION_VALUES;
  const features = featureCollection.features.filter((f) =>
    allowed.includes(f.properties?.node_type)
  );
  return { type: "FeatureCollection", features };
}

export async function downloadAsFormat(data, filenameBase, format) {
  const sanitized = filenameBase.replace(/[^\w.-]/g, "_");
  const dateStr = formatDate(new Date());

  switch (format) {
    case "json": {
      const str = JSON.stringify(data, null, 2);
      const blob = new Blob([str], { type: "application/json" });
      downloadBlob(blob, `${sanitized}_${dateStr}.json`);
      return;
    }
    case "geojson": {
      const str = JSON.stringify(data, null, 2);
      const blob = new Blob([str], { type: "application/geo+json" });
      downloadBlob(blob, `${sanitized}_${dateStr}.geojson`);
      return;
    }
    case "kml": {
      const fc = isFeatureCollection(data) ? data : toFeatureCollection(data);
      const kmlString = tokml(fc, { documentName: sanitized });
      const blob = new Blob([kmlString], {
        type: "application/vnd.google-earth.kml+xml",
      });
      downloadBlob(blob, `${sanitized}_${dateStr}.kml`);
      return;
    }
    case "shp": {
      const fc = isFeatureCollection(data) ? data : toFeatureCollection(data);
      if (!fc.features?.length) {
        return;
      }
      const zipBlob = await shpwrite.zip(fc, {
        outputType: "blob",
        filename: sanitized,
        types: {
          point: sanitized,
          polygon: sanitized,
          polyline: sanitized,
        },
      });
      downloadBlob(zipBlob, `${sanitized}_${dateStr}.zip`);
      return;
    }
    default:
      throw new Error(`Unsupported format: ${format}`);
  }
}

function isFeatureCollection(obj) {
  return obj && obj.type === "FeatureCollection" && Array.isArray(obj.features);
}

function toFeatureCollection(obj) {
  if (isFeatureCollection(obj)) return obj;
  if (obj?.type === "Feature")
    return { type: "FeatureCollection", features: [obj] };
  return { type: "FeatureCollection", features: [] };
}
